import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { RiderEarning, RiderEarningsSummary } from '../../services';

interface RiderEarningsTabProps {
  earnings: RiderEarning[];
  earningsSummary: RiderEarningsSummary | null;
  // Optional date filters
  dateFrom?: Date | null;
  dateTo?: Date | null;
  onDateFromChange?: (d: Date | null) => void;
  onDateToChange?: (d: Date | null) => void;
  onApplyFilters?: () => void;
  onClearFilters?: () => void;
}

const getStatusColor = (status?: string) => {
  switch ((status || '').toString().toUpperCase()) {
    case 'PENDING':
      return 'warning'
    case 'PAID':
      return 'success'
    case 'DEDUCTED':
      return 'error'
    case 'HOLD':
      return 'info'
    default:
      return 'default'
  }
}

const RiderEarningsTab: React.FC<RiderEarningsTabProps> = ({
  earnings,
  earningsSummary,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <Grid container spacing={3}>
      {/* Earnings Summary */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h6">Earnings Summary</Typography>
              {/* <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={earningsPeriod}
                  label="Period"
                  onChange={(e) =>
                    onPeriodChange(e.target.value as 'weekly' | 'monthly' | 'yearly')
                  }
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl> */}
            </Box>

            {earningsSummary && (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Earnings
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {formatCurrency(earningsSummary.totalEarnings)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Orders
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {earningsSummary.totalOrders}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Paid Amount
                      </Typography>
                      <Typography variant="h5">
                        {formatCurrency(earningsSummary.successAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Pending Amount
                      </Typography>
                      <Typography variant="h5" color="red">
                        {formatCurrency(
                          (earningsSummary.pendingEarnings as number) ?? (earningsSummary as any).pending ?? 0
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Date filters for earnings */}
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="From"
                          value={dateFrom}
                          onChange={(d) => onDateFromChange?.(d)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="To"
                          value={dateTo}
                          onChange={(d) => onDateToChange?.(d)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                      <Button variant="contained" onClick={() => onApplyFilters?.()} sx={{ mr: 1 }}>
                        Apply
                      </Button>
                      <Button variant="outlined" onClick={() => onClearFilters?.()}>
                        Clear
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Earnings Table */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              MIS Earnings Rows
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Client Rider ID</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Hub</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="right">Gross Payout</TableCell>
                    <TableCell align="right">Deduction</TableCell>
                    <TableCell align="right">Final Payout</TableCell>
                    <TableCell align="right">Final Deducted</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {earnings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                        No MIS earning rows found
                      </TableCell>
                    </TableRow>
                  ) : (
                    earnings.map((row: any) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.clientName || '-'}</TableCell>
                        <TableCell>{row.clientRiderId || '-'}</TableCell>
                        <TableCell>{row.periodStartDate ? new Date(row.periodStartDate).toLocaleDateString('en-IN') : '-'} - {row.periodEndDate ? new Date(row.periodEndDate).toLocaleDateString('en-IN') : '-'}</TableCell>
                        <TableCell>{row.Hub || '-'}</TableCell>
                        <TableCell>{row.City || '-'}</TableCell>
                        <TableCell align="right">{row.grossPayout != null ? new Intl.NumberFormat('en-IN').format(row.grossPayout) : '-'}</TableCell>
                        <TableCell align="right">{row.Deduction != null ? new Intl.NumberFormat('en-IN').format(row.Deduction) : '-'}</TableCell>
                        <TableCell align="right">{row.finalPayout != null ? new Intl.NumberFormat('en-IN').format(row.finalPayout) : '-'}</TableCell>
                        <TableCell align="right">{row.FinalDeducted != null ? new Intl.NumberFormat('en-IN').format(row.FinalDeducted) : '-'}</TableCell>
                        <TableCell align="right">{row.pending != null ? new Intl.NumberFormat('en-IN').format(row.pending) : '-'}</TableCell>
                        <TableCell align="right">
                          {row.status ? (
                            <Chip
                              label={row.status}
                              color={getStatusColor(row.status) as any}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>


    </Grid>
  );
};

export default RiderEarningsTab;
